import { useEffect, useMemo, useRef, useState } from 'react';
import { type OrderDetails, type OrderLeg } from '../services/orders.service';
import {
  computeOog,
  type OogComputeInput,
  type OogComputeLeg,
  type OogComputeResult,
} from '../services/oog.service';
import { formatMdYHm } from '../utils/dateUtils';

/**
 * Subset of the order-editing hook surface the orchestrator needs. Lets the
 * caller pass any compatible editing object — OrderFormEditing from
 * OrderFormView, the full useOrderEditing return, etc.
 */
export interface OverweightOrchestratorEditing {
  editedOrderFields: Record<string, unknown>;
  updateOrderField: (name: string, value: unknown) => void;
  effectiveLegs?: ReadonlyArray<OrderLeg>;
}

export interface OverweightToast {
  type: 'success' | 'warning';
  title: string;
  message?: string;
}

export interface UseOverweightOrchestratorResult {
  /**
   * Overweight per the full V1 calc (respects leg progress — already
   * delivered / transload clears). Drives the
   * editedOrderFields.overweightPermitRequired write and the toast.
   * Exposed for callers that want to render an overall indicator.
   */
  isOverweight: boolean;
  /**
   * Overweight ignoring leg progress — true whenever the cargo weight
   * would require a permit at any point in the move. Drives the red
   * highlight on weight inputs so dispatchers see the cue even when the
   * move is mid-delivery.
   */
  isOverweightForHighlight: boolean;
  /** Pounds over the matched threshold, when overweight. 0 otherwise. */
  overLbs: number;
  /**
   * Latest transition toast (null until a user-driven transition fires,
   * and cleared when `dismissToast` is called). Rendering is the caller's
   * responsibility — typically a Notification inside a portal.
   */
  toast: OverweightToast | null;
  dismissToast: () => void;
}

/** Debounce for the /oog/compute call — coalesces rapid weight/size keystrokes. */
const COMPUTE_DEBOUNCE_MS = 250;

/**
 * Realtime overweight orchestrator. Calls the CANONICAL backend compute
 * (POST /oog/compute — app/core/oog_specs.py), the exact same logic the
 * saverule `oog_checkspecs` runs, so the order form and the saverule can't
 * drift. (This replaces the former local TypeScript calc in
 * utils/overweightCheck.ts, which duplicated the Python threshold table.)
 *
 * Responsibilities (unchanged from the local-calc version):
 *   - Debounce order/leg edits and POST them to /oog/compute. One call returns
 *     both the leg-progress-respecting verdict (save flag + system approval) and
 *     the ignore-leg-progress verdict (persistent red weight highlight).
 *   - On transition, write `overweightPermitRequired` into the edit buffer and
 *     queue a toast.
 *   - When overweight + customer exception doesn't apply + approvedby is the
 *     system sentinel, clear the auto-approval ("System approval removed").
 *   - When a user edit lands back in customer-exception range, re-stamp the
 *     system approval ("System approval granted").
 *   - Suppress toasts/writes on initial load — only user-driven transitions
 *     produce a message.
 */
export function useOverweightOrchestrator(
  details: Partial<OrderDetails> | null,
  editing: OverweightOrchestratorEditing | undefined,
): UseOverweightOrchestratorResult {
  const d = details ?? {};
  const editedOrderFields = editing?.editedOrderFields ?? {};
  const updateOrderField = editing?.updateOrderField;

  // True once the user has touched any field that feeds the overweight /
  // exception calc. All write-and-toast effects below gate on this so they
  // don't fire on initial load (where the first compute arrives after mount
  // and would transiently shift the result, e.g. flipping exceptionApproved
  // long enough to trip the auto-clear and wipe the saved approval).
  const userEditedExceptionInput =
    editedOrderFields.lbs !== undefined ||
    editedOrderFields.size !== undefined ||
    editedOrderFields.equipType !== undefined ||
    editedOrderFields.chassisType !== undefined ||
    editedOrderFields.isDaycab !== undefined ||
    editedOrderFields.otr !== undefined ||
    editedOrderFields.dispatcher !== undefined;

  /** Effective value: edited wins, then saved. */
  const fv = <K extends keyof OrderDetails>(key: K): OrderDetails[K] | undefined =>
    (editedOrderFields[key as string] !== undefined
      ? editedOrderFields[key as string]
      : (d as Partial<OrderDetails>)[key]) as OrderDetails[K] | undefined;
  const s = (v: unknown): string => (v == null ? '' : String(v));
  const setField = (name: string, value: unknown) => updateOrderField?.(name, value);

  // ─────────────────────────────────────────────────────────────────────
  // Toast + transition seed. `wasOverweight === null` means the order
  // hasn't been seeded yet — the transition effect treats the first
  // settled compute as a silent seed (no toast, no flag write). Resetting to
  // null on pronumber change ensures every fresh order primes silently.
  // ─────────────────────────────────────────────────────────────────────
  const [wasOverweight, setWasOverweight] = useState<boolean | null>(null);
  const [toast, setToast] = useState<OverweightToast | null>(null);
  const dismissToast = () => setToast(null);

  // State for the set-direction approval effect below.
  //   hasSeededExceptionInputsRef — false until the effect runs once for
  //     a given pronumber; we skip its write on that first pass so the
  //     saved approval/datetime aren't clobbered on initial load.
  //   lastApprovalSignatureRef — JSON digest of the exception inputs at
  //     the time of the most recent write. Used to de-dup re-runs caused
  //     by unstable `editing` references on parent re-renders (our writes
  //     don't change our gating conditions, so without this the effect
  //     would loop and immediately reinstate the toast after dismiss).
  //   wasInExceptionRef — tracks whether the order was in exception range
  //     on the previous eval, so the toast fires only on the out→in
  //     transition rather than on every re-stamp keystroke.
  const hasSeededExceptionInputsRef = useRef(false);
  const lastApprovalSignatureRef = useRef<string | null>(null);
  const wasInExceptionRef = useRef(false);

  useEffect(() => {
    setWasOverweight(null);
    hasSeededExceptionInputsRef.current = false;
    lastApprovalSignatureRef.current = null;
    wasInExceptionRef.current = false;
  }, [d.pronumber]);

  // ─────────────────────────────────────────────────────────────────────
  // Compute inputs — pull effective values for everything user-editable,
  // saved values for the rest. Effective legs surface unsaved leg edits
  // (e.g. cleared arrival dates) so the compute reacts to them.
  // ─────────────────────────────────────────────────────────────────────
  const effectiveLegsForCalc = editing?.effectiveLegs ?? d.legs ?? [];

  // Saved destination-arrival per leg — used to mark a leg's arrival as
  // "unchanged this session" (v1's new==old test), so a freshly-entered
  // arrival doesn't count as "already delivered".
  const savedArrivalByLeg = useMemo(() => {
    const m = new Map<number, string>();
    (d.legs ?? []).forEach((l) => m.set(l.legnum, v2s(l.destArrivalDateTime)));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.legs]);

  const calcLegs = useMemo<OogComputeLeg[]>(
    () =>
      effectiveLegsForCalc.map((l) => {
        // v1's delivered check keys off the leg's DESTINATION arrival
        // (arrivaldatetime), not its origin arrival.
        const arrival = v2s(l.destArrivalDateTime);
        return {
          legNum: l.legnum,
          origCode: l.origcode,
          destCode: l.destcode,
          legAction2: l.legAction2,
          arrivalDateTime: arrival,
          arrivalUnchanged: arrival === (savedArrivalByLeg.get(l.legnum) ?? ''),
        };
      }),
    [effectiveLegsForCalc, savedArrivalByLeg],
  );

  const computeInput = useMemo<OogComputeInput | null>(
    () =>
      details == null
        ? null
        : {
            size: s(fv('size')),
            equipType: s(fv('equipType')),
            chassisType: s(fv('chassisType')),
            otr: s(fv('otr')),
            lbs: (fv('lbs') as number | undefined) ?? '',
            isDaycab: s(fv('isDaycab')),
            dispatcher: s(fv('dispatcher')),
            billTo: d.billTo ?? '',
            customer: d.customer ?? '',
            moveType: s(d.moveType),
            originateLeg: d.originateLeg ?? 0,
            deliveryLeg: d.deliveryLeg ?? 0,
            terminateLeg: d.terminateLeg ?? 0,
            legs: calcLegs,
            // ooglegal / flatbed dimensions are not on the /pro OrderDetails yet —
            // this orchestrator is the weight surface; oversize stays server-side.
          },
    // The compute closes over fv/s/d; depend on the raw inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editedOrderFields.lbs,
      editedOrderFields.size,
      editedOrderFields.equipType,
      editedOrderFields.chassisType,
      editedOrderFields.otr,
      editedOrderFields.isDaycab,
      editedOrderFields.dispatcher,
      d.lbs,
      d.size,
      d.equipType,
      d.chassisType,
      d.otr,
      d.isDaycab,
      d.billTo,
      d.customer,
      d.moveType,
      d.dispatcher,
      d.originateLeg,
      d.deliveryLeg,
      d.terminateLeg,
      calcLegs,
      details == null,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────
  // Debounced canonical compute. One /oog/compute call returns both the
  // leg-progress-respecting verdict and the highlight (ignore-leg-progress)
  // verdict. A request-id guard drops out-of-order responses; a failed call
  // keeps the last result so a network blip doesn't wipe the flag.
  // ─────────────────────────────────────────────────────────────────────
  const [result, setResult] = useState<OogComputeResult | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!computeInput) return undefined;
    const reqId = ++reqIdRef.current;
    const handle = setTimeout(() => {
      computeOog(computeInput)
        .then((res) => {
          if (reqId === reqIdRef.current) setResult(res);
        })
        .catch(() => {
          /* keep the last result — a fetch blip shouldn't clear the verdict */
        });
    }, COMPUTE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [computeInput]);

  const isOverweight = result?.overweight ?? false;
  const overLbs = result?.overLbs ?? 0;
  const reason = result?.reason ?? '';
  const exceptionApproved = result?.exceptionApproved ?? false;

  // ─────────────────────────────────────────────────────────────────────
  // Transition detection — write flag + queue toast. Silent on the seed
  // run for a fresh order so dispatchers don't see "no longer overweight"
  // just because a delivered move loaded.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!editing || !result) return;
    if (wasOverweight === null) {
      setWasOverweight(isOverweight);
      return;
    }
    if (isOverweight === wasOverweight) return;
    // Defer writes + toast to user-driven transitions only. If the result
    // flipped because the initial compute settled after mount, silently
    // update the tracker so the next legitimate user edit fires normally.
    if (!userEditedExceptionInput) {
      setWasOverweight(isOverweight);
      return;
    }
    if (isOverweight) {
      setField('overweightPermitRequired', 'Y');
      setToast({
        type: 'warning',
        title: 'Order is now overweight',
        message: `Over by ${overLbs.toLocaleString()} lbs (${reason})`,
      });
    } else {
      setField('overweightPermitRequired', 'N');
      setToast({
        type: 'success',
        title: 'Order is no longer overweight',
        message: reason,
      });
    }
    setWasOverweight(isOverweight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, isOverweight, overLbs, editing, wasOverweight, userEditedExceptionInput]);

  // ─────────────────────────────────────────────────────────────────────
  // System auto-approval clear. V1 source: cms.js:7641-7649. Only the
  // 'system' approval is cleared — manual user approvals stay put.
  // Without this clear, driver re-qual would short-circuit to qualified
  // because check_driver_qualification treats 'system' as approved.
  // ─────────────────────────────────────────────────────────────────────
  const effectiveApprovedBy = String(fv('overweightApprovedBy') ?? '');
  const effectiveApprovedDatetime = String(fv('overweightApprovedDatetime') ?? '');
  useEffect(() => {
    if (!editing || !result) return;
    if (!isOverweight) return;
    if (exceptionApproved) return;
    if (effectiveApprovedBy.toLowerCase() !== 'system') return;
    if (effectiveApprovedDatetime === '') return;
    // Defer to user-driven changes — without this, opening an order with a
    // saved 'system' approval while the first compute is still in flight reads
    // exceptionApproved as transiently false, clobbers the saved approval, and
    // pops a misleading "System approval removed" toast.
    if (!userEditedExceptionInput) return;

    setField('overweightApprovedBy', '');
    setField('overweightApprovedDatetime', '');
    setToast({
      type: 'warning',
      title: 'System approval removed',
      message: 'Exception weight limit exceeded — overweight permit will be required for approval.',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editing,
    result,
    isOverweight,
    exceptionApproved,
    effectiveApprovedBy,
    effectiveApprovedDatetime,
    userEditedExceptionInput,
  ]);

  // ─────────────────────────────────────────────────────────────────────
  // System auto-approval set — inverse of the auto-clear above. V1 parity:
  // when a user-driven edit lands the order back in customer-exception range
  // (overweight + match), write overweightApprovedBy='system' and re-stamp
  // overweightApprovedDatetime. Re-stamps even when already 'system' — V1
  // updates the timestamp on every relevant edit.
  //
  // Mutually exclusive with the auto-clear via the exceptionApproved check,
  // so the two effects can never both write in the same render.
  //
  // De-dup vs. parent re-renders: our writes don't change our gating
  // conditions, so the signature ref ensures one write per unique exception-
  // input state, and wasInException scopes the toast to the out→in transition.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!editing || !result) return;
    // Skip the seed eval. Without this, opening any in-exception order would
    // clobber the persisted approval/datetime on mount.
    if (!hasSeededExceptionInputsRef.current) {
      hasSeededExceptionInputsRef.current = true;
      wasInExceptionRef.current = !!(isOverweight && exceptionApproved);
      return;
    }
    // Require an actual user edit to one of the exception inputs. After a
    // save the edit buffer is cleared (lbs: 'X' → undefined), which would
    // otherwise look like an input change and trigger a spurious re-stamp.
    if (!userEditedExceptionInput) return;
    const inException = isOverweight && exceptionApproved;
    if (!inException) {
      // Falling out of exception: let the auto-clear above handle the write.
      wasInExceptionRef.current = false;
      lastApprovalSignatureRef.current = null;
      return;
    }
    // De-dup against parent re-renders that didn't actually change any
    // exception input.
    const signature = JSON.stringify([
      editedOrderFields.lbs ?? null,
      editedOrderFields.size ?? null,
      editedOrderFields.equipType ?? null,
      editedOrderFields.chassisType ?? null,
      editedOrderFields.isDaycab ?? null,
      editedOrderFields.otr ?? null,
      editedOrderFields.dispatcher ?? null,
    ]);
    if (signature === lastApprovalSignatureRef.current) return;
    lastApprovalSignatureRef.current = signature;
    setField('overweightApprovedBy', 'system');
    setField('overweightApprovedDatetime', formatMdYHm(new Date()));
    // Toast only on the out→in transition, not on every re-stamp.
    if (!wasInExceptionRef.current) {
      setToast({
        type: 'success',
        title: 'System approval granted',
        message: 'Order falls within a customer exception — overweight permit auto-approved by system.',
      });
    }
    wasInExceptionRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editing,
    result,
    editedOrderFields.lbs,
    editedOrderFields.size,
    editedOrderFields.equipType,
    editedOrderFields.chassisType,
    editedOrderFields.isDaycab,
    editedOrderFields.otr,
    editedOrderFields.dispatcher,
    isOverweight,
    exceptionApproved,
  ]);

  return {
    isOverweight,
    isOverweightForHighlight: result?.overweightForHighlight ?? false,
    overLbs,
    toast,
    dismissToast,
  };
}

/** Local string coercion usable inside useMemo callbacks (mirrors `s`). */
function v2s(v: unknown): string {
  return v == null ? '' : String(v);
}
